import React, { useState } from "react";
import TapCard from "./TapCard";

function CardGroup({ label, answerKey, options, answers, setAnswer, cols = 2, multiSelect = false }) {
  function handleSelect(optLabel) {
    if (multiSelect) {
      const current = answers[answerKey] || [];
      if (current.includes(optLabel)) {
        setAnswer(answerKey, current.filter((x) => x !== optLabel));
      } else {
        setAnswer(answerKey, [...current, optLabel]);
      }
    } else {
      setAnswer(answerKey, optLabel);
    }
  }

  const currentVal = answers[answerKey];

  return (
    <div style={{ marginBottom: "24px" }}>
      <label style={{ display: "block", fontWeight: 600, marginBottom: "12px", fontSize: "0.95rem" }}>
        {label}
      </label>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: "10px" }}>
        {options.map((opt, i) => {
          const optLabel = typeof opt === "string" ? opt : opt.label;
          const optEmoji = typeof opt === "string" ? undefined : opt.emoji;
          const isSelected = multiSelect
            ? (currentVal || []).includes(optLabel)
            : currentVal === optLabel;
          return (
            <TapCard
              key={`${optLabel}-${i}`}
              emoji={optEmoji}
              label={optLabel}
              selected={isSelected}
              onSelect={() => handleSelect(optLabel)}
              multiSelect={multiSelect}
            />
          );
        })}
      </div>
    </div>
  );
}

function TextAreaQuestion({ label, answerKey, placeholder, answers, setAnswer, minHeight = "100px", required = false, showCharCount = false, maxChars = 500 }) {
  const value = answers[answerKey] || "";
  return (
    <div style={{ marginBottom: "24px" }}>
      <label style={{ display: "block", fontWeight: 600, marginBottom: "8px", fontSize: "0.95rem" }}>
        {label} {!required && <span style={{ fontWeight: 400, color: "var(--muted, #6b7280)", fontSize: "0.85rem" }}>(optional)</span>}
      </label>
      <textarea
        value={value}
        onChange={(e) => setAnswer(answerKey, e.target.value)}
        placeholder={placeholder}
        style={{ width: "100%", minHeight, fontSize: "0.95rem", padding: "14px", borderRadius: "12px", border: "2px solid var(--border, #e5e7eb)", boxSizing: "border-box", resize: "vertical" }}
      />
      {showCharCount && (
        <div style={{ textAlign: "right", fontSize: "0.8rem", color: "var(--muted, #6b7280)", marginTop: "4px" }}>
          {value.length} / {maxChars} characters
        </div>
      )}
    </div>
  );
}

function PaintingQuestions({ answers, setAnswer }) {
  return (
    <>
      <CardGroup label="What needs painting?" answerKey="paintWhat" options={["Walls", "Ceilings", "Walls + ceilings", "Everything including trims"]} answers={answers} setAnswer={setAnswer} cols={2} />
      <CardGroup label="How many rooms?" answerKey="paintRooms" options={["1 room", "2–3 rooms", "4–5 rooms", "Whole house", "Not sure"]} answers={answers} setAnswer={setAnswer} cols={2} />
      <CardGroup label="What condition is it in?" answerKey="paintCondition" options={[{ emoji: "👍", label: "Good — just needs a refresh" }, { emoji: "😐", label: "Fair — some cracks or marks" }, { emoji: "😬", label: "Poor — lots of prep needed" }]} answers={answers} setAnswer={setAnswer} cols={1} />
      <CardGroup label="Do you have paint?" answerKey="paintSupply" options={[{ emoji: "✅", label: "I'll supply the paint" }, { emoji: "🛒", label: "Tradie to supply paint" }, { emoji: "🤷", label: "Not sure yet" }]} answers={answers} setAnswer={setAnswer} cols={1} />
      <TextAreaQuestion label="Anything else the tradie should know?" answerKey="paintNotes" placeholder="e.g. High ceilings, furnished home, specific colour needed" answers={answers} setAnswer={setAnswer} />
    </>
  );
}

function PlumbingQuestions({ answers, setAnswer }) {
  return (
    <>
      <CardGroup label="Is it an emergency?" answerKey="plumbUrgency" options={[{ emoji: "🚨", label: "Yes — water is leaking now" }, { emoji: "⏰", label: "Urgent — needs fixing soon" }, { emoji: "📅", label: "Can wait — book it in" }]} answers={answers} setAnswer={setAnswer} cols={1} />
      <CardGroup label="Which area?" answerKey="plumbArea" options={["Kitchen", "Bathroom", "Laundry", "Outside", "Multiple areas"]} answers={answers} setAnswer={setAnswer} cols={2} />
      <CardGroup label="How old is the property?" answerKey="plumbAge" options={["Under 10 years", "10–30 years", "30+ years", "Not sure"]} answers={answers} setAnswer={setAnswer} cols={2} />
      <TextAreaQuestion label="Describe the problem" answerKey="plumbNotes" placeholder="e.g. Dripping tap under kitchen sink, slow drain in shower" answers={answers} setAnswer={setAnswer} />
    </>
  );
}

function ElectricalQuestions({ answers, setAnswer }) {
  return (
    <>
      <CardGroup label="Is it a safety issue?" answerKey="elecSafety" options={[{ emoji: "⚡", label: "Yes — could be dangerous" }, { emoji: "🔧", label: "No — it's an upgrade or install" }, { emoji: "🤷", label: "Not sure" }]} answers={answers} setAnswer={setAnswer} cols={1} />
      <CardGroup label="Which part of the home?" answerKey="elecArea" options={["Kitchen", "Living room", "Bedroom", "Bathroom", "Outside", "Whole home"]} answers={answers} setAnswer={setAnswer} cols={2} />
      <CardGroup label="How old is the switchboard?" answerKey="elecBoard" options={["Modern (less than 10 years)", "Older (10–30 years)", "Very old (30+ years)", "Don't know"]} answers={answers} setAnswer={setAnswer} cols={2} />
      <TextAreaQuestion label="Anything else?" answerKey="elecNotes" placeholder="e.g. No power to back bedroom, want 3 new downlights in kitchen" answers={answers} setAnswer={setAnswer} />
    </>
  );
}

function CarpentryQuestions({ answers, setAnswer }) {
  return (
    <>
      <CardGroup label="Who supplies the materials?" answerKey="carpSupply" options={[{ emoji: "🪵", label: "Tradie supplies everything" }, { emoji: "🛒", label: "I'll buy the materials" }, { emoji: "🤝", label: "Let's discuss" }]} answers={answers} setAnswer={setAnswer} cols={1} />
      <CardGroup label="Is this structural or decorative?" answerKey="carpType" options={[{ emoji: "🏗️", label: "Structural (load bearing)" }, { emoji: "🎨", label: "Decorative (cosmetic)" }, { label: "Both" }, { label: "Not sure" }]} answers={answers} setAnswer={setAnswer} cols={2} />
      <TextAreaQuestion label="Describe the job" answerKey="carpNotes" placeholder="e.g. Replace 3 interior doors, install new skirting boards throughout" answers={answers} setAnswer={setAnswer} />
    </>
  );
}

function LandscapingQuestions({ answers, setAnswer }) {
  return (
    <>
      <CardGroup label="How big is the yard?" answerKey="landSize" options={["Small (under 100m²)", "Medium (100–300m²)", "Large (300m²+)", "Not sure"]} answers={answers} setAnswer={setAnswer} cols={2} />
      <CardGroup label="Is the block flat or sloped?" answerKey="landSlope" options={[{ emoji: "🟰", label: "Flat" }, { emoji: "↗️", label: "Slightly sloped" }, { emoji: "📐", label: "Steep block" }]} answers={answers} setAnswer={setAnswer} cols={1} />
      <CardGroup label="Is there vehicle/equipment access?" answerKey="landAccess" options={[{ emoji: "✅", label: "Easy — wide gate or open" }, { emoji: "⚠️", label: "Tight — narrow side access" }, { emoji: "❌", label: "No vehicle access" }]} answers={answers} setAnswer={setAnswer} cols={1} />
      <TextAreaQuestion label="Anything to keep or remove?" answerKey="landNotes" placeholder="e.g. Keep the big mango tree, remove the old shed" answers={answers} setAnswer={setAnswer} />
    </>
  );
}

function RoofingQuestions({ answers, setAnswer }) {
  return (
    <>
      <CardGroup label="How many storeys is the building?" answerKey="roofStoreys" options={[{ emoji: "🏠", label: "Single storey" }, { emoji: "🏢", label: "Double storey" }, { emoji: "🏗️", label: "More than 2" }]} answers={answers} setAnswer={setAnswer} cols={1} />
      <CardGroup label="What type of roof?" answerKey="roofType" options={[{ emoji: "🟫", label: "Terracotta tiles" }, { emoji: "⬜", label: "Concrete tiles" }, { emoji: "🔩", label: "Colorbond / metal" }, { emoji: "🏚️", label: "Fibro / old" }, { label: "Not sure" }]} answers={answers} setAnswer={setAnswer} cols={2} />
      <CardGroup label="When did you last have it inspected?" answerKey="roofInspect" options={["Recently (last 2 years)", "A while ago (2–10 years)", "Never / don't know"]} answers={answers} setAnswer={setAnswer} cols={1} />
      <TextAreaQuestion label="Describe the issue" answerKey="roofNotes" placeholder="e.g. Water coming through ceiling in back bedroom after rain" answers={answers} setAnswer={setAnswer} />
    </>
  );
}

function CleaningQuestions({ answers, setAnswer }) {
  return (
    <>
      <CardGroup label="How many bedrooms?" answerKey="cleanBeds" options={["Studio", "1 bed", "2 bed", "3 bed", "4 bed", "5+ bed"]} answers={answers} setAnswer={setAnswer} cols={3} />
      <CardGroup label="How many bathrooms?" answerKey="cleanBaths" options={["1", "2", "3", "4+"]} answers={answers} setAnswer={setAnswer} cols={4} />
      <CardGroup label="Any extras needed?" answerKey="cleanExtras" options={[{ emoji: "🍳", label: "Oven" }, { emoji: "🪟", label: "Windows" }, { emoji: "🧺", label: "Inside cupboards" }, { emoji: "🐾", label: "Pet hair" }, { emoji: "🪴", label: "Balcony" }]} answers={answers} setAnswer={setAnswer} cols={2} multiSelect={true} />
      <CardGroup label="When was it last professionally cleaned?" answerKey="cleanLast" options={["Recently", "6+ months ago", "Never", "It's a new build"]} answers={answers} setAnswer={setAnswer} cols={2} />
    </>
  );
}

function SolarQuestions({ answers, setAnswer }) {
  return (
    <>
      <CardGroup label="What type of roof do you have?" answerKey="solarRoof" options={[{ emoji: "🟫", label: "Terracotta tiles" }, { emoji: "⬜", label: "Concrete tiles" }, { emoji: "🔩", label: "Colorbond / metal" }, { label: "Not sure" }]} answers={answers} setAnswer={setAnswer} cols={2} />
      <CardGroup label="Do you have an existing solar system?" answerKey="solarExisting" options={[{ emoji: "✅", label: "Yes — looking to upgrade" }, { emoji: "❌", label: "No — first-time install" }]} answers={answers} setAnswer={setAnswer} cols={1} />
      <CardGroup label="Approximate system size?" answerKey="solarSize" options={["Small (up to 6.6kW)", "Medium (6.6–13kW)", "Large (13kW+)", "Not sure"]} answers={answers} setAnswer={setAnswer} cols={2} />
      <TextAreaQuestion label="Anything else?" answerKey="solarNotes" placeholder="e.g. Two-storey home, shading from nearby tree, want battery backup" answers={answers} setAnswer={setAnswer} />
    </>
  );
}

function BuildingQuestions({ answers, setAnswer }) {
  return (
    <>
      <CardGroup label="What stage are you at?" answerKey="buildStage" options={[{ emoji: "💡", label: "Early planning" }, { emoji: "📐", label: "Have plans ready" }, { emoji: "✅", label: "Ready to start" }]} answers={answers} setAnswer={setAnswer} cols={1} />
      <CardGroup label="Approximate project size?" answerKey="buildSize" options={["Small (under $50k)", "Medium ($50k–$200k)", "Large ($200k+)", "Not sure yet"]} answers={answers} setAnswer={setAnswer} cols={2} />
      <CardGroup label="Do you have council approval?" answerKey="buildApproval" options={[{ emoji: "✅", label: "Yes — approved" }, { emoji: "⏳", label: "Pending" }, { emoji: "❌", label: "Not yet" }, { emoji: "🤷", label: "Not sure if needed" }]} answers={answers} setAnswer={setAnswer} cols={2} />
      <TextAreaQuestion label="Describe the project" answerKey="buildNotes" placeholder="e.g. Single storey extension of 40m², open plan kitchen and living" answers={answers} setAnswer={setAnswer} />
    </>
  );
}

function ConcreterQuestions({ answers, setAnswer }) {
  return (
    <>
      <CardGroup label="Approximate area?" answerKey="concArea" options={["Under 20m²", "20–50m²", "50–100m²", "100m²+"]} answers={answers} setAnswer={setAnswer} cols={2} />
      <CardGroup label="What finish are you after?" answerKey="concFinish" options={[{ emoji: "🔲", label: "Plain / broom finish" }, { emoji: "✨", label: "Exposed aggregate" }, { emoji: "🎨", label: "Coloured / decorative" }, { emoji: "🤷", label: "Not sure yet" }]} answers={answers} setAnswer={setAnswer} cols={2} />
      <CardGroup label="Does the area need excavation?" answerKey="concExcavate" options={[{ emoji: "✅", label: "Yes" }, { emoji: "❌", label: "No" }, { emoji: "🤷", label: "Not sure" }]} answers={answers} setAnswer={setAnswer} cols={1} />
      <TextAreaQuestion label="Anything else?" answerKey="concNotes" placeholder="e.g. Replacing existing cracked driveway, sloped block, need drainage" answers={answers} setAnswer={setAnswer} />
    </>
  );
}

function HVACQuestions({ answers, setAnswer }) {
  return (
    <>
      <CardGroup label="What type of property?" answerKey="hvacProperty" options={[{ emoji: "🏠", label: "House" }, { emoji: "🏢", label: "Apartment / unit" }, { emoji: "🏪", label: "Commercial" }]} answers={answers} setAnswer={setAnswer} cols={1} />
      <CardGroup label="How many rooms to cool / heat?" answerKey="hvacRooms" options={["1 room", "2–3 rooms", "4–5 rooms", "Whole home"]} answers={answers} setAnswer={setAnswer} cols={2} />
      <CardGroup label="Do you have an existing system?" answerKey="hvacExisting" options={[{ emoji: "✅", label: "Yes — needs repair or replacement" }, { emoji: "❌", label: "No — new install" }]} answers={answers} setAnswer={setAnswer} cols={1} />
      <TextAreaQuestion label="Anything else?" answerKey="hvacNotes" placeholder="e.g. Reverse cycle preferred, high ceilings, need 3-phase power checked" answers={answers} setAnswer={setAnswer} />
    </>
  );
}

function HandymanQuestions({ answers, setAnswer, notesError }) {
  return (
    <>
      <CardGroup label="How many tasks do you have?" answerKey="handyCount" options={["Just one", "A small list (2–5)", "Lots of jobs"]} answers={answers} setAnswer={setAnswer} cols={1} />
      <CardGroup label="Do you need materials supplied?" answerKey="handySupply" options={[{ emoji: "✅", label: "Yes please" }, { emoji: "🛒", label: "I'll get the materials" }, { emoji: "🤷", label: "Mix of both" }]} answers={answers} setAnswer={setAnswer} cols={1} />
      <div style={{ marginBottom: "24px" }}>
        <label style={{ display: "block", fontWeight: 600, marginBottom: "8px", fontSize: "0.95rem" }}>
          Tell us what needs doing <span style={{ fontWeight: 400, color: "#ef4444", fontSize: "0.85rem" }}>*</span>
        </label>
        <textarea
          value={answers["handyNotes"] || ""}
          onChange={(e) => setAnswer("handyNotes", e.target.value.slice(0, 500))}
          placeholder="e.g. Hang 3 pictures, fix sticky bedroom door, patch hole in wall"
          style={{ width: "100%", minHeight: "120px", fontSize: "0.95rem", padding: "14px", borderRadius: "12px", border: `2px solid ${notesError ? "#ef4444" : "var(--border, #e5e7eb)"}`, boxSizing: "border-box", resize: "vertical" }}
        />
        <div style={{ textAlign: "right", fontSize: "0.8rem", color: "var(--muted, #6b7280)", marginTop: "4px" }}>
          {(answers["handyNotes"] || "").length} / 500 characters
        </div>
        {notesError && (
          <p style={{ color: "#ef4444", fontSize: "0.85rem", marginTop: "4px" }}>{notesError}</p>
        )}
      </div>
    </>
  );
}


export default function StepTradeDetails({ form, update, onNext, errors }) {
  const [notesError, setNotesError] = useState("");

  const answers = form.tradeSpecificAnswers || {};

  function setAnswer(key, value) {
    update("tradeSpecificAnswers", { ...answers, [key]: value });
  }

  function handleNext() {
    if (form.trade === "Handyman" && !(answers["handyNotes"] || "").trim()) {
      setNotesError("Please describe what needs doing");
      return;
    }
    setNotesError("");
    onNext();
  }

  return (
    <div>
      {form.trade === "Painting" && <PaintingQuestions answers={answers} setAnswer={setAnswer} />}
      {form.trade === "Plumbing" && <PlumbingQuestions answers={answers} setAnswer={setAnswer} />}
      {form.trade === "Electrical" && <ElectricalQuestions answers={answers} setAnswer={setAnswer} />}
      {form.trade === "Solar & Battery" && <SolarQuestions answers={answers} setAnswer={setAnswer} />}
      {form.trade === "Building" && <BuildingQuestions answers={answers} setAnswer={setAnswer} />}
      {form.trade === "Carpentry" && <CarpentryQuestions answers={answers} setAnswer={setAnswer} />}
      {form.trade === "Concreter" && <ConcreterQuestions answers={answers} setAnswer={setAnswer} />}
      {form.trade === "Handyman" && <HandymanQuestions answers={answers} setAnswer={setAnswer} notesError={notesError} />}
      {form.trade === "HVAC" && <HVACQuestions answers={answers} setAnswer={setAnswer} />}
      {form.trade === "Landscaping" && <LandscapingQuestions answers={answers} setAnswer={setAnswer} />}
      {form.trade === "Roofing" && <RoofingQuestions answers={answers} setAnswer={setAnswer} />}
      {form.trade === "Cleaning" && <CleaningQuestions answers={answers} setAnswer={setAnswer} />}

      <button
        type="button"
        onClick={handleNext}
        style={{
          background: "var(--primary)",
          color: "#fff",
          border: "none",
          borderRadius: "12px",
          padding: "18px 32px",
          fontSize: "1rem",
          fontWeight: 700,
          cursor: "pointer",
          width: "100%",
          marginTop: "8px",
        }}
      >
        Next →
      </button>
    </div>
  );
}
